/**
 * 稽核 AuditLogTab (S10.1) — the GLOBAL review lens.
 *
 * "報表送出給高權限看過": every reason-logged write across the app converges
 * here. Server rows (governance mutations + the seeded W9 price override / PO
 * release / taxonomy change) are merged with the LIVE PRICE_OVERRIDE rows the
 * pricing store accumulates during the session (usePriceOverrideAudit — S9.1/
 * S9.2 special-price + SO-line overrides), so a fresh override done in 定價設定
 * or SO entry shows up here with old→new + reason without any refetch.
 *
 * Filters: date range + actor + module. Drill routes to the owning module.
 * CSV export downloads the filtered rows (client-side).
 */

import { useMemo, useState } from 'react';
import { App, Button, DatePicker, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TableContainer, { ListEmptyState, renderDash } from '@/components/common/TableContainer';
import { controlHeightTokens } from '@/styles/tokens';
import { usePriceOverrideAudit } from '@/hooks/usePricing';
import { useAuditLogs } from './useSystemGovernance';
import type { AuditLogQuery, AuditModule, GovernanceAuditLog } from './model';

const { Text } = Typography;
const { RangePicker } = DatePicker;

const MODULES: readonly AuditModule[] = [
  'pricing',
  'procurement',
  'taxonomy',
  'product',
  'sales',
  'finance',
  'governance',
];

const OP_COLOR: Record<GovernanceAuditLog['operationType'], string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  APPROVE: 'purple',
  OVERRIDE: 'orange',
};

/** Does a client-side (pricing) row satisfy the active filters? */
function matchesQuery(row: GovernanceAuditLog, query: AuditLogQuery): boolean {
  if (query.dateFrom && row.createdAt < query.dateFrom) return false;
  if (query.dateTo && row.createdAt > `${query.dateTo}T23:59:59.999Z`) return false;
  if (query.actor && row.actorName !== query.actor) return false;
  if (query.module && query.module !== 'all' && row.module !== query.module) return false;
  return true;
}

/** Drill target for a row's owning module, or null when not addressable. */
function drillPath(row: GovernanceAuditLog): string | null {
  switch (row.module) {
    case 'pricing':
      return '/auth/pricing/setup/overrides';
    case 'procurement':
      return row.entityId.startsWith('po-')
        ? `/auth/purchase-orders/${row.entityId}`
        : '/auth/purchase-orders';
    case 'taxonomy':
      return '/auth/product-taxonomy';
    case 'product':
      return row.entityId.startsWith('prod-')
        ? `/auth/products/${row.entityId}`
        : '/auth/products';
    case 'sales':
      return '/auth/sales-orders';
    case 'finance':
      return '/auth/finance/invoices';
    case 'governance':
      return '/auth/governance/users';
    default:
      return null;
  }
}

const csvCell = (value?: string): string => `"${(value ?? '').replace(/"/g, '""')}"`;

const AuditLogTab = () => {
  const { t } = useTranslation('governance');
  const { message } = App.useApp();
  const navigate = useNavigate();

  const [query, setQuery] = useState<AuditLogQuery>({ module: 'all' });

  // Unfiltered feed powers the actor dropdown regardless of active filters.
  const { auditLogs: allRows } = useAuditLogs({ module: 'all' });
  const { auditLogs: serverRows, loading } = useAuditLogs(query);
  const pricingRows = usePriceOverrideAudit();

  // Live pricing-store rows mapped into the unified audit shape.
  const pricingAudit = useMemo<GovernanceAuditLog[]>(
    () =>
      pricingRows.map((row) => ({
        id: row.id,
        createdAt: row.createdAt,
        operationType: 'OVERRIDE',
        module: 'pricing',
        entityType: row.entityType,
        entityId: row.entityId,
        reason: row.reason,
        summary: row.summary,
        actorName: row.actorName,
      })),
    [pricingRows]
  );

  const rows = useMemo<GovernanceAuditLog[]>(() => {
    const server = serverRows ?? [];
    const seenIds = new Set(server.map((r) => r.id));
    const livePricing = pricingAudit.filter((r) => !seenIds.has(r.id) && matchesQuery(r, query));
    return [...server, ...livePricing].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }, [serverRows, pricingAudit, query]);

  const actorOptions = useMemo(() => {
    const set = new Set<string>();
    (allRows ?? []).forEach((r) => set.add(r.actorName));
    pricingAudit.forEach((r) => set.add(r.actorName));
    return [...set].map((name) => ({ label: name, value: name }));
  }, [allRows, pricingAudit]);

  const clearFilters = () => setQuery({ module: 'all' });

  const exportCsv = () => {
    if (!rows.length) {
      message.warning(t('messages.exportEmpty'));
      return;
    }
    const header = [
      t('fields.when'),
      t('fields.operation'),
      t('fields.module'),
      t('fields.entity'),
      t('fields.field'),
      t('fields.oldValue'),
      t('fields.newValue'),
      t('fields.reason'),
      t('fields.actor'),
    ];
    const lines = rows.map((r) =>
      [
        dayjs(r.createdAt).format('YYYY-MM-DD HH:mm'),
        t(`operation.${r.operationType}`),
        t(`modules.${r.module}`),
        r.entityLabel ?? `${r.entityType} · ${r.entityId}`,
        r.field ?? '',
        r.oldValue ?? '',
        r.newValue ?? '',
        r.reason ?? r.summary,
        r.actorName,
      ]
        .map(csvCell)
        .join(',')
    );
    // Lead with a UTF-8 BOM so Excel renders the zh columns correctly.
    const csv = `\uFEFF${[header.map(csvCell).join(','), ...lines].join('\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${t('audit.csvFilename')}-${dayjs().format('YYYYMMDD')}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<GovernanceAuditLog> = [
    {
      title: t('fields.when'),
      dataIndex: 'createdAt',
      width: 150,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
    {
      title: t('fields.operation'),
      dataIndex: 'operationType',
      width: 110,
      render: (op: GovernanceAuditLog['operationType']) => (
        <Tag color={OP_COLOR[op]}>{t(`operation.${op}`)}</Tag>
      ),
    },
    {
      title: t('fields.module'),
      dataIndex: 'module',
      width: 120,
      render: (moduleKey: AuditModule) => <Tag>{t(`modules.${moduleKey}`)}</Tag>,
    },
    {
      title: t('fields.entity'),
      key: 'entity',
      width: 220,
      render: (_, row) => row.entityLabel ?? `${row.entityType} · ${row.entityId}`,
    },
    { title: t('fields.field'), dataIndex: 'field', width: 130, render: renderDash },
    { title: t('fields.oldValue'), dataIndex: 'oldValue', width: 140, render: renderDash },
    { title: t('fields.newValue'), dataIndex: 'newValue', width: 140, render: renderDash },
    {
      title: t('fields.reason'),
      key: 'reason',
      render: (_, row) => row.reason ?? row.summary,
    },
    { title: t('fields.actor'), dataIndex: 'actorName', width: 130 },
    {
      title: '',
      key: 'drill',
      width: 80,
      fixed: 'right',
      render: (_, row) => {
        const path = drillPath(row);
        return (
          <Button type="link" disabled={!path} onClick={() => path && navigate(path)}>
            {t('actions.drill')}
          </Button>
        );
      },
    },
  ];

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        {t('audit.hint')}
      </Text>

      <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-justify-between">
        <Space wrap>
          <RangePicker
            size="large"
            value={
              query.dateFrom && query.dateTo
                ? [dayjs(query.dateFrom), dayjs(query.dateTo)]
                : null
            }
            onChange={(range) =>
              setQuery((prev) => ({
                ...prev,
                dateFrom: range?.[0]?.format('YYYY-MM-DD'),
                dateTo: range?.[1]?.format('YYYY-MM-DD'),
              }))
            }
          />
          <Select
            size="large"
            allowClear
            style={{ width: 200 }}
            placeholder={t('audit.filters.allActors')}
            value={query.actor}
            onChange={(actor) => setQuery((prev) => ({ ...prev, actor }))}
            options={actorOptions}
          />
          <Select
            size="large"
            style={{ width: 180 }}
            value={query.module ?? 'all'}
            onChange={(module) => setQuery((prev) => ({ ...prev, module }))}
            options={[
              { label: t('modules.all'), value: 'all' },
              ...MODULES.map((m) => ({ label: t(`modules.${m}`), value: m })),
            ]}
          />
          <Button
            size="large"
            style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
            onClick={clearFilters}
          >
            {t('actions.clearFilters')}
          </Button>
        </Space>
        <Button
          size="large"
          icon={<DownloadOutlined />}
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
          onClick={exportCsv}
        >
          {t('actions.exportCsv')}
        </Button>
      </div>

      <TableContainer>
        <Table<GovernanceAuditLog>
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => String(total) }}
          scroll={{ x: 1340 }}
          locale={{ emptyText: <ListEmptyState title={t('audit.noRows')} /> }}
        />
      </TableContainer>
    </div>
  );
};

export default AuditLogTab;
